import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  FileScan,
  Folder,
  Link as LinkIcon,
  PanelLeftClose,
  Share2,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { useTranslation } from '@/i18n'
import { pathAllowed } from '@/features/featureAccess/features'
import { useFeatureAccess } from '@/features/featureAccess/useFeatureAccess'

type DataNavItem = {
  /**
   * Taken from the redesign's 10-1 frame, which puts a glyph on every item.
   *
   * Note `Data` is a FOLDER here, not the `Database` cylinder the app rail
   * uses for the same word -- the frame draws them differently on purpose, so
   * do not "correct" one to match the other.
   */
  icon: LucideIcon
  labelKey: string
  defaultLabel: string
  path: string
  disabled?: boolean
}

type DataNavSection = {
  titleKey: string
  defaultTitle: string
  items: DataNavItem[]
}

const SECTIONS: DataNavSection[] = [
  {
    // Reuse the registered `nav` namespace (data_nav was never registered, so
    // these labels silently fell back to English — FS-532).
    titleKey: 'nav:sections.data_onboarding',
    defaultTitle: 'Data Onboarding',
    items: [
      { icon: Folder, labelKey: 'nav:menu.data', defaultLabel: 'Data', path: '/data-catalog' },
      // Re-enabled with the self-service Eventhouse lane (was FS-579-disabled
      // until the flow was productized).
      { icon: LinkIcon, labelKey: 'nav:menu.connectors', defaultLabel: 'Connectors', path: '/connectors' },
      { icon: Users, labelKey: 'nav:menu.expert_knowledge', defaultLabel: 'Knowledge Capture', path: '/human-expert-knowledge' },
    ],
  },
  {
    titleKey: 'nav:sections.data_contextualization',
    defaultTitle: 'Data Contextualization',
    items: [
      // Assets Hierarchy was removed here: interactive P&IDs had already moved
      // into the Data Catalog file-type filter, `/assets-hierarchy` is not a
      // registered route, and the entry had been sitting permanently disabled.
      // A nav item that can never be clicked and leads nowhere is worse than no
      // entry at all. `disabled` stays supported on the type for the next one.
      // FS-655: the standing review surface for entity matching. The Data
      // Catalog's "Match Entity" bulk action still RUNS matching over a
      // selection; this is where the results get confirmed or corrected.
      { icon: FileScan, labelKey: 'nav:menu.tag_mapping', defaultLabel: 'Tag Mapping', path: '/tag-mapping' },
      { icon: Share2, labelKey: 'nav:menu.knowledge_graph', defaultLabel: 'Knowledge Graph', path: '/data/graph' },
    ],
  },
]

const STORAGE_KEY = 'data-sidebar-collapsed'

/**
 * Fired to bring the panel back after it has been hidden.
 *
 * Hiding removes the panel ENTIRELY — the collapse control goes with it — so
 * nothing inside this component can reopen it, and the preference survives a
 * reload. The way back is the app rail's "Data" item, which is where someone
 * looks anyway once the section nav is gone.
 *
 * A window event rather than a storage listener: `localStorage` only fires
 * `storage` in OTHER tabs, so the tab that wrote it would never hear itself.
 */
const REVEAL_EVENT = 'data-sidebar:reveal'

/** Reopen the Data section nav. Safe to call when it is already open. */
export const revealDataSidebar = (): void => {
  try {
    localStorage.setItem(STORAGE_KEY, '0')
  } catch {
    // Storage unavailable — the event below still reopens it for this session.
  }
  window.dispatchEvent(new Event(REVEAL_EVENT))
}

// Below this width both sidebars leave the content too cramped, so the
// sub-sidebar auto-collapses to its icon rail.
const NARROW_QUERY = '(max-width: 1023px)'

const prefersCollapsed = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

const DataSidebar = () => {
  const { t } = useTranslation()
  const { isEnabled } = useFeatureAccess()
  const visible = (path: string) => pathAllowed(path, isEnabled)
  const navigate = useNavigate()
  const location = useLocation()
  // Account for a narrow viewport up front to avoid a collapse flash on mount.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      if (window.matchMedia(NARROW_QUERY).matches) return true
    } catch {
      // matchMedia unavailable (e.g. jsdom) — fall back to preference
    }
    return prefersCollapsed()
  })

  // Auto-collapse when narrow; restore the saved preference when widened.
  // Auto-collapse never persists, so a manual expand survives a resize.
  useEffect(() => {
    let mq: MediaQueryList
    try {
      mq = window.matchMedia(NARROW_QUERY)
    } catch {
      return
    }
    const apply = () => setCollapsed(mq.matches ? true : prefersCollapsed())
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  // Reopened from the app rail — see REVEAL_EVENT.
  useEffect(() => {
    const onReveal = () => setCollapsed(false)
    window.addEventListener(REVEAL_EVENT, onReveal)
    return () => window.removeEventListener(REVEAL_EVENT, onReveal)
  }, [])

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem(STORAGE_KEY, next ? '1' : '0')
      } catch {
        // ignore
      }
      return next
    })
  }

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`)

  // COLLAPSED MEANS GONE, not narrowed. This used to fall back to a 48px icon
  // rail; the design hides the panel outright, so hiding it halfway left a
  // column of unlabelled glyphs that is neither the nav nor out of the way.
  //
  // The expand control therefore cannot live inside this component — there is
  // nothing left to hang it on. It sits in the page header instead, which is
  // also the only place a reader would look for it once the panel is gone.
  if (collapsed) return null

  return (
    <aside className="w-[256px] shrink-0 border-r border-border bg-background dark:bg-dark-card flex flex-col py-4 px-3 gap-6">
      <div className="flex items-center justify-between px-1">
        <span className="ps-2 text-sm font-semibold text-foreground">
          {t('nav:menu.data', { defaultValue: 'Data' })}
        </span>
        <button
          type="button"
          onClick={toggle}
          className="size-7 rounded-md hover:bg-muted flex items-center justify-center text-muted-foreground"
          aria-label={t('nav:data_sidebar.collapse', { defaultValue: 'Collapse sub-sidebar' })}
        >
          <PanelLeftClose className="size-4" />
        </button>
      </div>
      {SECTIONS.map((section) => (
        <div key={section.titleKey} className="flex flex-col gap-1">
          <div className="px-3 pb-1 text-xs font-medium text-muted-foreground">
            {t(section.titleKey, { defaultValue: section.defaultTitle })}
          </div>
          {section.items.filter((item) => visible(item.path)).map((item) => {
            const active = isActive(item.path)
            const Icon = item.icon
            return (
              <button
                key={item.path}
                type="button"
                disabled={item.disabled}
                aria-current={active ? 'page' : undefined}
                onClick={() => !item.disabled && navigate(item.path)}
                className={[
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-left transition-colors',
                  item.disabled
                    ? 'text-muted-foreground/60 cursor-not-allowed'
                    : active
                      ? 'bg-cyan-50 text-cyan-700 dark:bg-sky-900/30 dark:text-cyan-300'
                      : 'text-foreground hover:bg-gray-50 dark:hover:bg-dark-muted',
                ].join(' ')}
              >
                <Icon className="size-4 shrink-0" aria-hidden="true" />
                <span className="truncate">
                  {t(item.labelKey, { defaultValue: item.defaultLabel })}
                </span>
              </button>
            )
          })}
        </div>
      ))}
    </aside>
  )
}

export default DataSidebar
